/**
 * ProjectSwitcher - Global project switching modal
 * 
 * Triggered by Cmd+P (Mac) or Ctrl+P (Windows/Linux) or Projects tab
 * Provides quick fuzzy search to switch between projects with detailed info
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { projectsApi } from '../../services/api';
import type { ProjectInstance } from '../../types';
import type { ActiveProject } from '../../contexts/AIContext';
import { Button, Input } from '@/components/ui';
import { logger } from '../../utils/logger';
import { useAutocompleteKeyboard } from '../../hooks/useAutocompleteKeyboard';

interface ProjectSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchProject: (projectId: string | null) => void;
  activeProject: ActiveProject | null;
  onCreateProject?: () => void;
  onBrowseAll?: () => void;
}

export function ProjectSwitcher({
  isOpen,
  onClose,
  onSwitchProject,
  activeProject,
  onCreateProject,
  onBrowseAll,
}: ProjectSwitcherProps) {
  const [query, setQuery] = useState('');
  const [projects, setProjects] = useState<ProjectInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  
  // Fetch projects when modal opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      projectsApi.listMyProjects()
        .then((data) => {
          setProjects(data);
        })
        .catch((err) => {
          logger.error('[ProjectSwitcher] Failed to load projects:', err);
          setError('Failed to load projects');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen]);
  
  // Filter projects by query
  const filteredProjects = projects.filter(p =>
    p.projectCode.toLowerCase().includes(query.toLowerCase()) ||
    (p.customerName || '').toLowerCase().includes(query.toLowerCase()) ||
    (p.projectTitle || '').toLowerCase().includes(query.toLowerCase())
  );
  
  // Build items list: "Close Project" option (if active) + filtered projects - memoized to prevent dependency changes
  const items = useMemo<Array<{ type: 'close' | 'project'; project?: ProjectInstance }>>(() => {
    const result: Array<{ type: 'close' | 'project'; project?: ProjectInstance }> = [];
    
    if (activeProject && (!query || 'close project'.includes(query.toLowerCase()) || 'global'.includes(query.toLowerCase()))) {
      result.push({ type: 'close' });
    }
    
    filteredProjects.forEach(project => {
      result.push({ type: 'project', project });
    });
    
    return result;
  }, [activeProject, query, filteredProjects]);
  
  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);
  
  // Handle selection (defined before hook that uses it)
  const handleSelect = useCallback((item: { type: 'close' | 'project'; project?: ProjectInstance }) => {
    if (item.type === 'close') {
      onSwitchProject(null);
    } else if (item.project) {
      onSwitchProject(item.project.id);
    }
    onClose();
  }, [onSwitchProject, onClose]);

  const handleKeySelect = useCallback((index: number) => {
    if (items[index]) {
      handleSelect(items[index]);
    }
  }, [items, handleSelect]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: items.length,
    onSelect: handleKeySelect,
    onClose,
    enabled: isOpen,
    resetDeps: [query],
  });
  
  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center pt-[10vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal - wider to accommodate more details */}
      <div
        ref={containerRef}
        className="relative w-full max-w-2xl bg-card rounded-xl shadow-2xl border border-border overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center px-4 py-3 border-b border-border">
          <span className="text-muted-foreground mr-3">📁</span>
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects..."
            className="flex-1 bg-transparent border-none shadow-none text-base focus-visible:ring-0"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-muted border border-border rounded text-xs text-muted-foreground">
            esc
          </kbd>
        </div>
        
        {/* Current Project Indicator */}
        {activeProject && (
          <div className="px-4 py-2 bg-primary/10 border-b border-border text-sm">
            <span className="text-muted-foreground">Current: </span>
            <span className="font-medium text-primary">{activeProject.projectCode}</span>
            {activeProject.customer && (
              <span className="text-muted-foreground"> - {activeProject.customer}</span>
            )}
          </div>
        )}
        
        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center text-muted-foreground">
              <div className="text-3xl mb-2 animate-pulse">📁</div>
              <p>Loading projects...</p>
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-destructive">
              <div className="text-3xl mb-2">⚠️</div>
              <p>{error}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted-foreground">
              <div className="text-3xl mb-2">🔍</div>
              <p>No projects found{query ? ` for "${query}"` : ''}</p>
            </div>
          ) : (
            <>
              {items.map((item, idx) => {
                const isSelected = selectedIndex === idx;
                
                if (item.type === 'close') {
                  return (
                    <Button
                      key="close-project"
                      data-index={idx}
                      onClick={() => handleSelect(item)}
                      variant="ghost"
                      className={`w-full text-left px-4 py-3 h-auto flex items-center gap-3 justify-start rounded-none border-b border-border ${
                        isSelected
                          ? 'bg-destructive/10 text-destructive'
                          : 'text-destructive hover:bg-destructive/5'
                      }`}
                    >
                      <span className="text-lg w-6 text-center">✕</span>
                      <div className="flex-1">
                        <div className="font-medium">Close Project</div>
                        <div className="text-xs opacity-70">Return to project list</div>
                      </div>
                      {isSelected && (
                        <span className="text-xs text-muted-foreground">↵</span>
                      )}
                    </Button>
                  );
                }
                
                const project = item.project!;
                const isCurrentProject = activeProject?.projectId === project.id;
                const docCount = project.documents?.length || 0;
                
                return (
                  <Button
                    key={project.id}
                    data-index={idx}
                    onClick={() => handleSelect(item)}
                    variant="ghost"
                    className={`w-full text-left px-4 py-3 h-auto flex items-start gap-3 justify-start rounded-none ${
                      isSelected
                        ? 'bg-primary/10 text-primary'
                        : isCurrentProject
                          ? 'bg-primary/5 text-foreground'
                          : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <span className="text-lg w-6 text-center flex-shrink-0 mt-0.5">
                      {isCurrentProject ? '✓' : '📁'}
                    </span>
                    <div className="flex-1 min-w-0">
                      {/* Project Code + Current Badge */}
                      <div className="font-medium flex items-center gap-2 mb-1">
                        <span className="truncate">{project.projectCode}</span>
                        {isCurrentProject && (
                          <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded flex-shrink-0">
                            Current
                          </span>
                        )}
                      </div>
                      
                      {/* Project Title */}
                      {project.projectTitle && (
                        <div className="text-sm text-foreground truncate mb-1">
                          {project.projectTitle}
                        </div>
                      )}
                      
                      {/* Customer + Program */}
                      <div className="text-xs text-muted-foreground flex items-center gap-2 mb-1">
                        {project.customerName && (
                          <span className="truncate">
                            🏢 {project.customerName}
                          </span>
                        )}
                        {project.customerName && project.program && (
                          <span>•</span>
                        )}
                        {project.program && (
                          <span className="truncate">
                            ✈️ {project.program}
                          </span>
                        )}
                      </div>
                      
                      {/* Stats Row: Doc Count + Cert Authority */}
                      <div className="text-xs text-muted-foreground flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          📄 {docCount} doc{docCount !== 1 ? 's' : ''}
                        </span>
                        {project.certificationAuthority && (
                          <span className="flex items-center gap-1">
                            🎖️ {project.certificationAuthority}
                          </span>
                        )}
                      </div>
                      
                      {/* Certification Basis Tags (compact) */}
                      {project.certificationBasis && project.certificationBasis.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {project.certificationBasis.slice(0, 4).map(basis => (
                            <span
                              key={basis}
                              className="text-[10px] px-1.5 py-0.5 bg-info-light text-info rounded"
                            >
                              {basis}
                            </span>
                          ))}
                          {project.certificationBasis.length > 4 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">
                              +{project.certificationBasis.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Right side: enter indicator */}
                    {isSelected && (
                      <span className="text-xs text-muted-foreground flex-shrink-0 mt-1">↵</span>
                    )}
                  </Button>
                );
              })}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-2 border-t border-border bg-muted flex items-center justify-between text-xs">
          <div className="flex items-center gap-4 text-muted-foreground">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-card border border-border rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-card border border-border rounded">↓</kbd>
              <span className="ml-1">Navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-card border border-border rounded">↵</kbd>
              <span className="ml-1">Select</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground">{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
            {onBrowseAll && (
              <Button
                onClick={() => {
                  onClose();
                  onBrowseAll();
                }}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-info hover:bg-info-light"
              >
                <span>📋</span>
                <span>Browse All</span>
              </Button>
            )}
            {onCreateProject && (
              <Button
                onClick={() => {
                  onClose();
                  onCreateProject();
                }}
                size="sm"
                className="flex items-center gap-1"
              >
                <span>+</span>
                <span>New Project</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ProjectSwitcher;
